import type {
    AiAgentFormId,
    AiAgentIntent,
    AiAgentIntentMeta,
    AiAgentUiActionPayload,
} from '../../../redux/aiAgent/aiAgent.type';
import store from '../../../store';

/**
 * The server-owned intent catalog (GET aiAgentMeta) — null until the first fetch lands.
 * Every helper here degrades to "unknown" rather than assuming a hardcoded intent list.
 */
const getAgentMeta = () => store.getState().aiAgent?.meta ?? null;

export const getIntentMeta = (intent: AiAgentIntent | null | undefined): AiAgentIntentMeta | null =>
    (intent && getAgentMeta()?.intents.find((meta) => meta.intent === intent)) || null;

/** The server sanitizes intent/formId values — the guard only rules out empty/absent ones. */
export const isAiAgentIntent = (value: unknown): value is AiAgentIntent =>
    typeof value === 'string' && value.length > 0;

/** Generic wording for an intent the fetched meta does not know (or meta not loaded yet). */
const FALLBACK_CONFIRM_LABEL_KEY = 'proposalConfirm';

export const getIntentConfirmLabelKey = (intent: AiAgentIntent): string =>
    getIntentMeta(intent)?.confirmLabelKey ?? FALLBACK_CONFIRM_LABEL_KEY;

/**
 * The intent creates its record as one of a set of values (eventType first) — read from the
 * server's discriminator, with the legacy flag as fallback for servers that predate it.
 */
const hasDiscriminator = (meta: AiAgentIntentMeta): boolean =>
    !!meta.discriminator || meta.requiresEventTypes;

/** The discriminator's value list — package-scoped allowedEventTypes for the first implementation. */
const getDiscriminatorAllowedValues = (): string[] =>
    getAgentMeta()?.allowedEventTypes ?? [];

/**
 * Whether the server would accept this intent in the given scope (mirrors its
 * scopes.allowedIntents) — so the UI never offers an action that is bound to be refused.
 */
export const isIntentAvailableInScope = (
    intent: AiAgentIntent,
    scope: 'account' | 'event',
): boolean => {
    const meta = getIntentMeta(intent);
    if(!meta) {
        return false;
    }
    if(scope === 'account' && meta.requiresEvent) {
        return false;
    }
    return !(hasDiscriminator(meta) && !getDiscriminatorAllowedValues().length);
};

export const getFormIdForIntent = (intent: AiAgentIntent | null | undefined): AiAgentFormId | null =>
    getIntentMeta(intent)?.formId ?? null;

export const getIntentForFormId = (formId: string | null | undefined): AiAgentIntent | null => (
    (formId && getAgentMeta()?.intents.find((meta) => meta.formId === formId)?.intent) || null
);

/** The intent whose record is created as a chosen value — for now, the one creating an event. */
export const getDiscriminatorIntent = (): AiAgentIntent | null =>
    getAgentMeta()?.intents.find(hasDiscriminator)?.intent ?? null;

/** Locked chat intent, or intent of the last proposal's form. */
export const getEffectiveSessionIntent = (): AiAgentIntent | null => {
    const {aiAgent} = store.getState();
    return aiAgent?.sessionIntent ?? getIntentForFormId(aiAgent?.activeFormId);
};

/**
 * Which intent a uiAction belongs to. The server already sanitizes payload.intent/formId
 * against the allowed intents. eventType alone implies create-event.
 */
export const getPayloadIntent = (payload: AiAgentUiActionPayload = {}): AiAgentIntent | null => {
    if(isAiAgentIntent(payload.intent)) {
        return payload.intent;
    }
    const fromFormId = getIntentForFormId(payload.formId);
    if(fromFormId) {
        return fromFormId;
    }
    return payload.eventType ? getDiscriminatorIntent() : null;
};
